import { ref, onMounted, computed } from 'vue'
import axios from 'axios'

// Store notifications
const notifications = ref([])

// Computed property for unread count
const unreadCount = computed(() => {
  return notifications.value.filter(n => !n.read_at).length
})

/**
 * Gets a fresh auth token from localStorage or cookies
 */
const getAuthToken = () => {
  return localStorage.getItem('accessToken') || document.cookie.split('; ')
    .find(row => row.startsWith('accessToken='))
    ?.split('=')[1] || ''
}

/**
 * Standardized API request with auth headers
 */
const apiRequest = async (method, endpoint, data = null) => {
  const token = getAuthToken()

  const config = {
    method,
    url: endpoint.startsWith('/') ? endpoint : `/${endpoint}`,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  }
  
  if (data) {
    config.data = data
  }
  
  return axios(config)
}

/**
 * Check if a notification ID is from the server
 */
const isServerNotification = id => {
  // Check UUID pattern (Laravel notifications use UUIDs)
  const uuidPattern = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i
  const isUuid = uuidPattern.test(String(id))
  
  // Check for numeric database ID (but not timestamp-like IDs)
  const numericId = parseInt(id)

  const isValidDbId = !isNaN(numericId) && 
                     numericId > 0 && 
                     numericId < 1000000000 && // Less than 1 billion (excludes timestamps)
                     String(id).length < 10     // Less than 10 digits
  
  // Don't try to mark timestamp-based IDs as read on server
  const isTimestamp = !isNaN(numericId) && numericId > 1000000000000 // Timestamp-like number
  
  return (isUuid || isValidDbId) && !isTimestamp
}

/**
 * Format a notification for consistent structure
 */
const formatNotification = notification => {
  // Check if notification comes from different sources and handle accordingly
  const data = notification.detail || notification.data || notification

  return {
    id: notification.id, // No fallback ID generation - require proper server ID
    type: data.type || 'notification',
    title: data.title || 'New Notification',
    message: data.message || 'No content',
    parcel_id: data.parcel_id,
    package_id: data.package_id,
    timestamp: notification.created_at || notification.timestamp || new Date().toISOString(),
    read: !!notification.read_at,
    read_at: notification.read_at,
  }
}

/**
 * Play notification sound
 */
let notificationSound

const playNotificationSound = () => {
  try {
    if (!notificationSound) {
      notificationSound = new Audio('/audio/notification.mp3')
    }
  } catch (e) {
    // Handle silently
  }
}

/**
 * Fetch notifications from API
 *
 * KILL SWITCH (2026-05-08): the warehouse-staging team doesn't use the
 * notification feed today, and three different components mount this
 * composable so the same `/api/notifications` endpoint fires three times
 * back-to-back on every page load (~1.5s of redundant requests visible
 * in the network panel). Until a real notification UX is needed, the
 * fetch is a no-op. Local `addNotification()` calls still work — they
 * update the in-memory `notifications` ref directly without hitting the
 * server. Flip this flag to re-enable.
 */
const NOTIFICATIONS_REMOTE_ENABLED = false

const fetchNotifications = async () => {
  if (!NOTIFICATIONS_REMOTE_ENABLED) {
    return
  }
  try {
    const response = await apiRequest('get', '/notifications')

    if (response.data && Array.isArray(response.data)) {
      // Process server notifications properly
      const processedNotifications = response.data.map(notification => {
        // Keep the original data intact for localization
        return {
          id: notification.id,
          type: notification.type || (notification.data?.type) || 'notification',

          // Store the original data for localization
          data: notification.data || {},

          // Keep these for backward compatibility
          title: notification.data?.title || 'Notification',
          message: notification.data?.message || '',
          parcel_id: notification.data?.parcel_id,
          package_id: notification.data?.package_id,
          timestamp: notification.created_at || new Date().toISOString(),
          read: !!notification.read_at,
          read_at: notification.read_at,
          created_at: notification.created_at,
          source: 'server' // Mark as server-sourced
        }
      })
      
      notifications.value = processedNotifications
    }
  } catch (error) {
    // Handle error silently
  }
}

/**
 * Mark a notification as read
 */
const markRead = async notificationId => {
  try {
    // Update local state first for responsive UI
    notifications.value = notifications.value.map(n =>
      n.id === notificationId ? { ...n, read: true, read_at: new Date().toISOString() } : n,
    )
    
    // Call API if it's a server notification (kill switch: skip server sync
    // when the remote feed is disabled — we still update local UI above).
    const isServer = isServerNotification(notificationId)

    if (isServer && NOTIFICATIONS_REMOTE_ENABLED) {
      const response = await apiRequest('post', `/notifications/${notificationId}/mark-read`)
      
      // Check if the request was successful
      if (response.data && !response.data.success) {
        console.warn('WARNING: Could not mark notification as read on server:', response.data.message)

        // Don't throw error - local state is already updated
      }
    } else {
    }
  } catch (error) {
    console.warn('WARNING: Failed to mark notification as read on server:', error.response?.data?.message || error.message)

    // Don't throw the error - the notification is marked as read locally
    // This prevents the UI from breaking when server-side marking fails
  }
}

/**
 * Mark all notifications as read
 */
const markAllRead = async () => {
  try {
    // Prevent multiple calls by tracking the operation
    if (markAllRead._isRunning) {
      console.warn('markAllRead already running, skipping duplicate call')
      return
    }
    markAllRead._isRunning = true
    
    // Update all local notifications first for responsive UI
    notifications.value = notifications.value.map(n => ({
      ...n,
      read: true,
      read_at: n.read_at || new Date().toISOString(),
    }))
    
    // Call API to mark all as read (kill switch: skip when remote disabled).
    if (!NOTIFICATIONS_REMOTE_ENABLED) {
      return
    }
    const response = await apiRequest('post', '/notifications/mark-all-read')
    
    // Only refresh if the server operation was successful
    if (response.data?.success) {
      // Small delay to prevent race conditions
      setTimeout(async () => {
        await fetchNotifications()
        
        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('notifications:markAllRead'))
      }, 100)
    }
    
  } catch (error) {
    console.warn('Failed to mark all notifications as read:', error.message)
    // Don't revert local state - user already sees them as read
  } finally {
    // Reset the running flag after a delay to prevent rapid successive calls
    setTimeout(() => {
      markAllRead._isRunning = false
    }, 1000)
  }
}

/**
 * Remove a notification
 */
const removeNotification = async notificationId => {
  try {
    // Remove from local state first for responsive UI
    notifications.value = notifications.value.filter(n => n.id !== notificationId)
    
    // Call API if it's a server notification (kill switch: skip when disabled).
    if (isServerNotification(notificationId) && NOTIFICATIONS_REMOTE_ENABLED) {
      try {
        await apiRequest('delete', `/notifications/${notificationId}`)
      } catch (apiError) {
        if (apiError.response?.status === 404) {
          // Refresh the notification list to sync with server state
          setTimeout(() => {
            fetchNotifications()
          }, 500)
        }
        // Don't re-throw - local deletion already succeeded
      }
    }
  } catch (error) {
    // Handle error silently
  }
}

/**
 * Add a new notification
 */
const addNotification = notification => {
  // Validate notification has proper structure
  if (!notification || !notification.id) {
    console.warn('WARNING: Received notification without proper ID - skipping client-side creation')
    
    return
  }
  
  // Extract data from different possible sources
  const data = notification.data || notification.detail?.data || {}
  
  // Only process server notifications with proper IDs (UUIDs or proper database IDs)
  const hasValidServerId = (

    // UUID pattern
    /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(String(notification.id)) ||

    // Small database ID (not timestamp)
    (!isNaN(parseInt(notification.id)) && parseInt(notification.id) < 1000000000 && String(notification.id).length < 10)
  )
  
  if (!hasValidServerId) {
    console.warn('WARNING: Skipping client-side notification with invalid ID:', notification.id)
    
    return
  }
  
  // Process the notification
  const formattedNotification = {
    id: notification.id,
    type: notification.type || data.type || 'notification',

    // Keep the original data intact for proper localization
    data: {
      ...data,
      title: data.title || notification.title,
      message: data.message || notification.message,
      title_key: data.title_key,
      message_key: data.message_key,
      message_data: data.message_data || {},
      type: data.type || notification.type,
      parcel_id: data.parcel_id || notification.parcel_id,
      package_id: data.package_id || notification.package_id,
    },

    // Keep backward compatibility with title/message direct properties
    title: notification.title || data.title || 'New Notification',
    message: notification.message || data.message || '',
    parcel_id: notification.parcel_id || data.parcel_id,
    package_id: notification.package_id || data.package_id,
    timestamp: notification.created_at || notification.timestamp || new Date().toISOString(),
    read: !!notification.read_at,
    read_at: notification.read_at,
    created_at: notification.created_at || new Date().toISOString(),
  }

  // Check for duplicates (same type and created within the last 5 seconds)
  const now = Date.now()

  const isDuplicate = notifications.value.some(n => {
    // Compare type
    const isSameType = n.type === formattedNotification.type
    
    // Compare IDs for parcels and packages
    const isSameParcel = n.parcel_id && n.parcel_id === formattedNotification.parcel_id
    const isSamePackage = n.package_id && n.package_id === formattedNotification.package_id
    
    // Check if recent
    const createdAt = new Date(n.timestamp).getTime()
    const isRecent = (now - createdAt) < 5000
    
    return (isSameType && (isSameParcel || isSamePackage) && isRecent)
  })
  
  if (!isDuplicate) {
    // Add to notifications
    notifications.value.unshift(formattedNotification)
    
    // Limit to 20 notifications
    if (notifications.value.length > 20) {
      notifications.value.pop()
    }
    
    // Play sound
    playNotificationSound()
    
    // Notify other components
    window.dispatchEvent(new CustomEvent('notification:added', { 
      detail: formattedNotification,
    }))
  }
}

/**
 * Setup global notification handlers for use across components
 */
const setupGlobalHandlers = () => {
  // Register global notification functions
  window.notificationFunctions = {
    addNotification,
    refreshNotifications: fetchNotifications,
    markRead,
    markAllRead,
    removeNotification,
  }
  
  // Listen for WebSocket notifications
  window.addEventListener('notification', event => {
    if (event.detail) {
      // Make sure we're using the notification detail directly
      const notificationData = event.detail

      addNotification(notificationData)
    }
  })
}

// Initialize handlers when the composable is first used
let initialized = false

const initialize = () => {
  if (!initialized) {
    setupGlobalHandlers()
    fetchNotifications()
    initialized = true
  }
}

export function useNotifications() {
  // Initialize on first use
  initialize()
  
  return {
    notifications,
    unreadCount,
    addNotification,
    fetchNotifications,
    markRead,
    markAllRead,
    removeNotification,
  }
}
